import Account from 'app/src/data/models/Account';
import Localize from 'app/src/localize/Localize';
import Session from 'shared_modules/Session';
import Utility from 'shared_modules/Utility';
import $ from 'jquery';

let _instance = null;

const _apiKey = 'LHZfu9Nj6Irf3EfF3cj18i9aeIzm0hBB';
const _companyName = 'Lightspeed';
const _productName = 'Lightspeed Retail';

// Version doesn't have much context right now with Retail.  Perhaps, once a version is developed we
// can change this to pull from the version source.
const _productVersion = '1.0';

const PAIRING_API_FILENAME = 'iclient-v1.js';
const PAYMENT_API_FILENAME = 'iclient-with-ui-v1.js';
const SIMULATED_GATEWAY_URL = 'https://iclientsimulator.test.tyro.com/';
const TEST_GATEWAY_URL = 'https://iclient.test.tyro.com/';
const PROD_GATEWAY_URL = 'https://iclient.tyro.com/';

const _PairingStatus = {
  IN_PROGRESS: 'inProgress',
  SUCCESS: 'success',
  FAILURE: 'failure',
};

const _TransactionStatus = {
  APPROVED: 'APPROVED',
  CANCELLED: 'CANCELLED',
  REVERSED: 'REVERSED',
  DECLINED: 'DECLINED',
  SYSTEM_ERROR: 'SYSTEM ERROR',
  NOT_STARTED: 'NOT STARTED',
  UNKNOWN: 'UNKNOWN',
};

export default class TyroTerminalService {
  static get TransactionStatus() {
    return _TransactionStatus;
  }

  /**
   * Retrieves the singleton instance of the TyroTerminalService.  This is constructed as
   * a singleton because we can only have one instance of the Tyro API active at once.  We
   * obtain the instance in the resolution of a promise because we much fetch the Account ID
   * to determine if we should use the test API code or the the production API code and we
   * must fetch the appropriate script from the Tyro service.
   */
  static getInstance() {
    let deferred = new $.Deferred();
    if (_instance) {
      deferred.resolve(_instance);
    } else {
      let account = new Account({
        systemCustomerID: Session.accountID(),
      });

      account.fetch().always(() => {
        let tyroServicePromise = new TyroTerminalService(account.get('status'));
        tyroServicePromise.done(tyroServiceInstance => {
          _instance = tyroServiceInstance;
          deferred.resolve(tyroServiceInstance);
        });
      });
    }

    return deferred.promise();
  }

  /**
   * Constructs a TyroTerminalService instance
   *
   * @param {string} accountType      The type of account for the logged in user.  This is used
   *                                  to select the appropriate Tyro API library when used in a
   *                                  test vs production environment.
   * @returns {*}
   */
  constructor(accountType) {
    let deferred = $.Deferred();

    let tyroApiUrl = accountType === Account.AccountType.TEST ? TEST_GATEWAY_URL : PROD_GATEWAY_URL;
    // let tyroApiUrl = SIMULATED_GATEWAY_URL;
    Utility.fetchScript(`${tyroApiUrl}${PAYMENT_API_FILENAME}`).done(() => {
      // and initialize
      this._TYRO_UI = TYRO;

      this._tyroWithUIInstance = new this._TYRO_UI.IClientWithUI(_apiKey, {
        posProductVendor: _companyName,
        posProductName: _productName,
        posProductVersion: _productVersion,
      });

      Utility.fetchScript(`${tyroApiUrl}${PAIRING_API_FILENAME}`).done(() => {
        // and initialize
        this._TYRO = TYRO;

        this._tyroInstance = new this._TYRO.IClient(_apiKey, {
          posProductVendor: _companyName,
          posProductName: _productName,
          posProductVersion: _productVersion,
        });

        deferred.resolve(this);
      });
    });

    return deferred.promise();
  }

  /**
   * Attempts to pair the specified Tyro terminal ID using the configured merchant ID.  The promise
   * is resolved when the pairing is successful and will return the integration key that must be used
   * to initiate transactions with the terminal.  The promise will be rejected with the the error reported
   * from the Tyro API when the terminal pairing has failed.  Progress notifications will be sent during
   * the pair process containing the state text reported by the Tyro API.
   *
   * @param {string} mid      The merchant ID
   * @param {string} tid      The terminal ID
   * @returns {jQuery.promise}
   */
  pair(mid, tid) {
    let deferred = $.Deferred();

    // we can only every have one pair operation in progress at a time
    if (!this._pairPromise) {
      this._pairPromise = deferred;

      this._tyroInstance.pairTerminal(mid, tid, this._pairResponseCallback.bind(this));
    } else {
      deferred.reject(Localize.translate('Pairing already in progress.'));
    }

    return deferred.promise();
  }

  /**
   * Cancels a pairing operation
   */
  cancelPairing() {
    if (this._pairPromise) {
      this._pairPromise = null;
    }
  }

  /**
   * Starts a transaction with a supported and configured Tyro terminal.
   *
   * @param {Decimal} amount  The amount to transact
   * @param {CCTerminal} terminal     The model of the terminal used to transact the request
   * @returns {*}
   */
  startTransaction(amount, terminal) {
    let deferred = $.Deferred();
    let tyroApiTransactionMethod = amount.isNegative()
      ? this._tyroWithUIInstance.initiateRefund
      : this._tyroWithUIInstance.initiatePurchase;

    tyroApiTransactionMethod(
      {
        amount: amount
          .abs()
          .times(100)
          .toFixed(0), // Tyro expects amount in cents
        integratedReceipt: false,
        mid: terminal.configuration.merchantID,
        tid: terminal.configuration.terminalID,
        integrationKey: terminal.configuration.integrationKey,
      },
      {
        transactionCompleteCallback: deferred.resolve,
      }
    );

    return deferred.promise();
  }

  /**
   * The callback handler that is invoked by the Tyro API.  We've adapted it to use promises
   * so it's easier to deal with in our code.
   *
   * @param {Object} response     The response from the Tyro API
   * @private
   */
  _pairResponseCallback(response = {}) {
    switch (response.status) {
      case _PairingStatus.IN_PROGRESS:
        this._pairPromise.notify(response.message);
        break;

      case _PairingStatus.SUCCESS:
        this._pairPromise.resolve(response.integrationKey);
        this._pairPromise = null;
        break;

      case _PairingStatus.FAILURE:
        this._pairPromise.reject(response.message);
        this._pairPromise = null;
        break;

      default:
        console.log('An unknown status was received from the Tyro gateway.');
        break;
    }
  }
}
